import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import toast from "react-hot-toast";
import SavePresetModal from "./SavePresetModal";
import ManagePresetsModal from "./ManagePresetsModal";
import { SavedFilterPreset } from "../../slices/user-settings";
import { useSavedFilters } from "../../hooks/useSavedFilters";
import { constants } from "../../utils/constants/general";

interface SavedFiltersControlsProps<TFilters> {
  page: "contracts" | "grants";
  currentFilters: TFilters;
  // Called on "Save as preset" to merge the drawer form state with `filters` state
  // (drawer form is canonical for fields rendered in the drawer; `filters` state is
  // canonical for header-toggled fields like include*). Side effect: this should also
  // commit the merged values to `filters` so the dropdown immediately matches the new preset.
  getFiltersToSave: () => TFilters;
  onPresetSelected: (preset: SavedFilterPreset<TFilters>) => void;
}

const NONE_VALUE = "__none__";

// Normalize a filter value into a comparable form so values that round-trip through the URL
// (DropdownOption[] → string[], Date → ISO string, "" ↔ null) still match the original preset.
const normalizeFilterValue = (value: any): any => {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) {
    return value
      .map((v) =>
        v && typeof v === "object" && "value" in v ? v.value : v,
      )
      .map((v) => (v == null ? "" : String(v)))
      .sort();
  }
  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
      const d = new Date(value);
      if (!isNaN(d.getTime())) return d.toISOString();
    }
    return value;
  }
  return value;
};

const filterSignature = (filters: any): string => {
  const keys = Object.keys(filters).sort();
  return JSON.stringify(
    keys.map((k) => [k, normalizeFilterValue(filters[k])]),
  );
};

function SavedFiltersControls<TFilters>(
  props: SavedFiltersControlsProps<TFilters>,
) {
  const { page, currentFilters, getFiltersToSave, onPresetSelected } = props;
  const {
    payload,
    isLoading,
    isSaving,
    save,
    rename,
    remove,
    setDefault,
    isNameTaken,
  } = useSavedFilters<TFilters>(page);

  const [saveOpen, setSaveOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  const sortedPresets = useMemo(
    () =>
      [...payload.presets].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
      ),
    [payload.presets],
  );

  // Detect which preset (if any) matches the currently-applied filters.
  // Why: filters come from URL on reload — they round-trip through the query string and lose
  // their original DropdownOption / Date shapes. The normalizer makes the comparison robust.
  const currentSignature = useMemo(
    () => filterSignature(currentFilters),
    [currentFilters],
  );
  const matchingPresetId = useMemo(() => {
    const match = payload.presets.find(
      (p) => filterSignature(p.filters) === currentSignature,
    );
    return match?.id ?? NONE_VALUE;
  }, [payload.presets, currentSignature]);

  const handleSelect = (id: string) => {
    if (id === NONE_VALUE) return;
    const preset = payload.presets.find((p) => p.id === id);
    if (preset) onPresetSelected(preset);
  };

  const handleSaveSubmit = async (input: {
    name: string;
    setAsDefault: boolean;
  }) => {
    try {
      await save({
        name: input.name,
        filters: getFiltersToSave(),
        setAsDefault: input.setAsDefault,
      });
      toast.success("Preset saved");
      setSaveOpen(false);
    } catch {
      toast.error("Couldn't save preset. Try again.");
    }
  };

  const handleRename = async (id: string, name: string) => {
    try {
      await rename(id, name);
      toast.success("Preset renamed");
    } catch {
      toast.error("Couldn't rename preset. Try again.");
    }
  };

  const handleRemove = async (id: string) => {
    try {
      await remove(id);
      toast.success("Preset deleted");
    } catch {
      toast.error("Couldn't delete preset. Try again.");
    }
  };

  const handleSetDefault = async (id: string | null) => {
    try {
      await setDefault(id);
    } catch {
      toast.error("Couldn't update default. Try again.");
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <BookmarkBorderIcon fontSize={"small"} color={"action"} />
        <Typography
          variant={"caption"}
          sx={{ fontWeight: 600, letterSpacing: "0.05em" }}
        >
          SAVED FILTERS
        </Typography>
      </Box>
      <FormControl size={"small"} fullWidth>
        <InputLabel id={`saved-filters-${page}-label`}>
          {sortedPresets.length === 0
            ? "No saved presets"
            : "Apply a saved preset"}
        </InputLabel>
        <Select
          labelId={`saved-filters-${page}-label`}
          value={matchingPresetId}
          label={
            sortedPresets.length === 0
              ? "No saved presets"
              : "Apply a saved preset"
          }
          disabled={isLoading || sortedPresets.length === 0}
          onChange={(e) => handleSelect(e.target.value as string)}
          MenuProps={{
            sx: { zIndex: constants.zIndexes.drawerComboboxList },
          }}
          endAdornment={
            isLoading ? (
              <CircularProgress size={14} sx={{ mr: 3 }} />
            ) : undefined
          }
        >
          <MenuItem value={NONE_VALUE}>
            <em>None</em>
          </MenuItem>
          {sortedPresets.map((preset) => (
            <MenuItem key={preset.id} value={preset.id}>
              {preset.name}
              {payload.defaultId === preset.id ? " (default)" : ""}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Button
          variant={"text"}
          color={"secondary"}
          size={"small"}
          onClick={() => setSaveOpen(true)}
          disabled={isLoading}
          sx={{ textTransform: "none" }}
        >
          Save current as preset…
        </Button>
        <Link
          component={"button"}
          type={"button"}
          variant={"caption"}
          color={"secondary"}
          underline={"hover"}
          onClick={() => setManageOpen(true)}
          sx={{
            visibility: sortedPresets.length === 0 ? "hidden" : "visible",
          }}
        >
          Manage
        </Link>
      </Box>

      <SavePresetModal
        open={saveOpen}
        saving={isSaving}
        isNameTaken={(name) => isNameTaken(name)}
        onSubmit={handleSaveSubmit}
        onClose={() => setSaveOpen(false)}
      />
      <ManagePresetsModal<TFilters>
        open={manageOpen}
        saving={isSaving}
        payload={payload}
        isNameTaken={isNameTaken}
        onRename={handleRename}
        onRemove={handleRemove}
        onSetDefault={handleSetDefault}
        onClose={() => setManageOpen(false)}
      />
    </Box>
  );
}

export default SavedFiltersControls;
